import React from "react";
import { IAnalysisSummaryResponse } from "../../../../interface/analysisSummary";
import { REIProgressSpinner } from "../../../../components/ui";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store";
import { formatCurrency } from "../../../../utils/commonUtil";

interface SummaryRowProps {
  label: string;
  value: string | number | undefined;
  bgColor: string;
  border?: string;
}

const removePercentSign = (value: string | number | undefined): string | number | undefined => {
  if (typeof value === "string") {
    return value.replace("%", "");
  }
  return value;
};

const SummaryRow: React.FC<SummaryRowProps> = ({
  label,
  value,
  bgColor,
  border = "border-solid border border-l-0 border-b-0 border-gray-300",
}) => (
  <div className={`flex-0 md:w-1/3 w-full flex items-center pl-2 ${bgColor} ${border}`}>
    <div className="w-[50%]">
      <span className="font-semibold">{label}</span>
    </div>
    <div className={`w-[50%] text-end mr-2 ${Number(removePercentSign(value)) < 0 ? "text-red-600" : ""}`}>
      {Number(removePercentSign(value)) < 0 ? `(${Math.abs(Number(removePercentSign(value)))})` : value}
    </div>
  </div>
);

const SummaryRowPortfolio: React.FC<SummaryRowProps> = ({
  label,
  value,
  bgColor,
  border = "border-solid border border-l-0 border-b-0 border-gray-300",
}) => (
  <div className={`flex-0 md:w-1/2 w-full flex items-center pl-2 ${bgColor} ${border}`}>
    <div className="w-[50%]">
      <span className="font-semibold">{label}</span>
    </div>
    <div className={`w-[50%] text-end mr-2 ${Number(removePercentSign(value)) < 0 ? "text-red-600" : ""}`}>
      {Number(removePercentSign(value)) < 0 ? `(${Math.abs(Number(removePercentSign(value)))})` : value}
    </div>
  </div>
);

const SummaryInput: React.FC<{ isfromPortfolioTracker?: boolean; isEditable?: boolean }> = ({
  isfromPortfolioTracker,
  isEditable,
}) => {
  const analysisSummary = useSelector((state: RootState) => state.analyser.analysisSummary as IAnalysisSummaryResponse);

  if (!analysisSummary) {
    return (
      <div className="w-full text-center">
        <REIProgressSpinner />
      </div>
    );
  }

  const { TenYrInternalRate, Deposit, TenYrTotalGrowth, PurchasePrice, PurchaseCost, LoanCoast } =
    analysisSummary?.SummaryInput || {};

  return (
    <>
      {(!isfromPortfolioTracker || !isEditable) && (
        <>
          <div className="flex md:flex-row flex-col w-full min-h-9">
            <SummaryRow
              label="10yr internal rate of return"
              value={`${parseFloat(TenYrInternalRate?.toString() ?? "0").toFixed(2)}%`}
              bgColor="bg-slate-100"
            />
            <SummaryRow label="Deposit" value={formatCurrency(Deposit)} bgColor="bg-slate-50" />
            <SummaryRow
              label="10yr total growth"
              value={`${parseFloat(TenYrTotalGrowth?.toString() ?? "0").toFixed(2)}%`}
              bgColor="bg-slate-100"
              border="border-solid border border-x-0 border-b-0 border-gray-300"
            />
          </div>
          <div className="flex md:flex-row flex-col w-full min-h-9">
            <SummaryRow label="Property purchase price" value={formatCurrency(PurchasePrice)} bgColor="bg-slate-50" />
            <SummaryRow label="Purchase costs" value={formatCurrency(PurchaseCost)} bgColor="bg-slate-100" />
            <SummaryRow
              label="Loan cost"
              value={formatCurrency(LoanCoast)}
              bgColor="bg-slate-50"
              border="border-solid border border-x-0 border-b-0 border-gray-300"
            />
          </div>
        </>
      )}
      {isfromPortfolioTracker && isEditable && (
        <>
          <div className="flex md:flex-row flex-col w-full min-h-9">
            <SummaryRowPortfolio
              label="10yr internal rate of return"
              value={`${parseFloat(TenYrInternalRate?.toString() ?? "0").toFixed(2)}%`}
              bgColor="bg-slate-100"
            />
            <SummaryRowPortfolio
              label="Current loan outstanding"
              value={formatCurrency(analysisSummary?.AnalysisOverview.AmountLoanToday)}
              bgColor="bg-slate-50"
            />
          </div>
          <div className="flex md:flex-row flex-col w-full min-h-9">
            <SummaryRowPortfolio
              label="Current valuation"
              value={formatCurrency(analysisSummary?.AnalysisOverview.marketValueToday)}
              bgColor="bg-slate-50"
            />

            <SummaryRowPortfolio
              label="Loan cost"
              value={formatCurrency(LoanCoast)}
              bgColor="bg-slate-50"
              border="border-solid border border-x-0 border-b-0 border-gray-300"
            />
          </div>
        </>
      )}
    </>
  );
};

export default SummaryInput;
